/* eslint-disable no-param-reassign */
import { BehaviorSubject } from 'rxjs';

import BaseService from './base.service';

export default class CasesService extends BaseService {
  getCases(criteria) {
    const queryString = this.getQueryString(criteria);
    return this.get(`/cases${queryString}`);
  }

  getNotPaidCases() {
    return this.get('/cases/not-paid');
  }

  getCase(id, token = null) {
    return this.get(`/cases/${id}`, token);
  }

  claimCase(id) {
    return this.put(`/cases/${id}/claim`, {});
  }

  rejectCase(id) {
    return this.put(`/cases/${id}/reject`, {});
  }

  discardReview(id) {
    return this.put(`/cases/${id}/discard-review`, {});
  }

  sendForReview(id) {
    return this.put(`/cases/${id}/done`, {});
  }

  startReview(id) {
    return this.put(`/cases/${id}/review`, {});
  }

  cancelReview(id) {
    return this.put(`/cases/${id}/cancel-review`, {});
  }

  signOff(id) {
    return this.put(`/cases/${id}/sign-off`, {});
  }

  revertSignOff(caseId) {
    return this.put(`/cases/${caseId}/revert-sign-off`, {});
  }

  getLinkViewer(id) {
    return this.get(`/cases/${id}/viewer-link`);
  }

  rejectPendingUploadCase(id) {
    return this.put(`/cases/${id}/clinic-upload/reject`);
  }

  archiveCase(id) {
    return this.put(`/cases/${id}/archive`);
  }

  unarchiveCase(id) {
    return this.put(`/cases/${id}/unarchive`);
  }

  submitRate(id, reportRate) {
    return this.put(`/cases/${id}/rate`, reportRate);
  }

  bypassImagesAndReport(id) {
    return this.put(`/cases/${id}/upload-shared/by-pass`);
  }

  submitCase(payload, progress$ = new BehaviorSubject(0)) {
    const formData = this.payloadToFormData(payload, ['files', 'uploadedReport', 'patient', 'additionalFiles', 'notifiedAdmins']);

    (payload.notifiedAdmins || []).forEach((adminId, index) => formData.append(`notifiedAdmins[${index}]`, adminId));
    (payload?.files || []).forEach((file) => formData.append(`files`, file));
    (payload?.uploadedReport || []).forEach((file) => formData.append(`reportFiles`, file));

    let fileIndex = 0;
    (payload?.additionalFiles || []).forEach((aF, index) => {
      const typeKey = `additionalFilesDetail[${index}][type]`;
      const indexKey = `additionalFilesDetail[${index}][filesIndex]`;

      const currentFilesIndex = [];

      aF.files.forEach((file) => {
        currentFilesIndex.push(fileIndex);
        formData.append('additionalFiles', file);
        fileIndex += 1;
      });

      formData.append(typeKey, aF.type);
      formData.append(indexKey, currentFilesIndex.join(','));
    });

    this.addObjectToFormData(formData, payload?.patient || payload?.claimant, payload.patient ? 'patient' : 'claimant');

    if (payload.uploadSharedInfo) {
      this.addObjectToFormData(formData, payload.uploadSharedInfo, 'uploadSharedInfo');
    }

    if (payload.caseLinkInfo) {
      this.addObjectToFormData(formData, payload.caseLinkInfo, 'caseLinkInfo');
    }

    return this.sendFormData('POST', payload?.patient ? '/cases' : 'cases/law-firm', formData, progress$);
  }

  requestVideoReport(payload, progress$ = new BehaviorSubject(0)) {
    const formData = this.payloadToFormData(payload, ['files', 'uploadedReport', 'patient']);

    (payload?.files || []).forEach((file) => formData.append(`files`, file));

    const pattern = /([^A-Za-z0-9\-.\s])+/;
    (payload?.uploadedReport || []).forEach((file) => {
      const isContainsSpecialCharacters = pattern.test(file.name);
      let fileToUpload = file;
      if (isContainsSpecialCharacters) {
        fileToUpload = new File([file], encodeURIComponent(file.name), {
          lastModified: file.lastModified,
          type: file.type,
        });
      }

      formData.append('reportFiles', fileToUpload);
    });

    this.addObjectToFormData(formData, payload.patient, 'patient');

    return this.sendFormData('POST', '/cases/request-report', formData, progress$);
  }

  requestUploadSharedReport(payload, progress$ = new BehaviorSubject(0)) {
    const formData = this.payloadToFormData(payload, ['uploadedReport', 'patient', 'uploadSharedInfo']);

    this.addObjectToFormData(formData, payload.patient, 'patient');
    this.addObjectToFormData(formData, payload.uploadSharedInfo, 'uploadSharedInfo');

    payload.uploadedReport.forEach((file) => formData.append(`reportFiles`, file));

    return this.sendFormData('POST', '/cases/request-report/upload-shared', formData, progress$);
  }

  requestUploadSharedLinkReport(payload, progress$ = new BehaviorSubject(0)) {
    const formData = this.payloadToFormData(payload, ['uploadedReport', 'patient', 'caseLinkInfo']);

    this.addObjectToFormData(formData, payload.patient, 'patient');
    this.addObjectToFormData(formData, payload.caseLinkInfo, 'caseLinkInfo');

    payload.uploadedReport.forEach((file) => formData.append(`reportFiles`, file));

    return this.sendFormData('POST', '/cases/request-report/upload-shared-link', formData, progress$);
  }

  completeUploadSharedReportForPatient(payload) {
    return this.post(`/cases/${payload.caseId}/complete-upload-shared`, payload);
  }

  completeCase(payload, progress$ = new BehaviorSubject(0)) {
    const formData = this.payloadToFormData(payload, ['files', 'additionalFiles', 'uploadedReport', 'patient', 'caseId', 'notifiedAdmins']);

    (payload.notifiedAdmins || []).forEach((adminId, index) => {
      formData.append(`notifiedAdmins[${index}]`, adminId);
    });

    payload.files.forEach((file) => {
      formData.append(`files`, file);
    });

    (payload.uploadedReport || []).forEach((file) => formData.append(`files`, file));

    let fileIndex = 0;
    (payload.additionalFiles || []).forEach((aF, index) => {
      const typeKey = `additionalFilesDetail[${index}][type]`;
      const indexKey = `additionalFilesDetail[${index}][filesIndex]`;

      const currentFilesIndex = [];

      aF.files.forEach((file) => {
        currentFilesIndex.push(fileIndex);
        formData.append('additionalFiles', file);
        fileIndex += 1;
      });

      formData.append(typeKey, aF.type);
      formData.append(indexKey, currentFilesIndex.join(','));
    });

    this.addObjectToFormData(formData, payload.patient, 'patient');

    return this.sendFormData('PUT', `/cases/${payload.caseId}/complete`, formData, progress$);
  }

  submitClinicUploadFiles(payload, progress$ = new BehaviorSubject(0)) {
    const formData = new FormData();
    if (payload.files) {
      payload.files.forEach((file) => {
        formData.append('files', file);
      });
    }

    if (payload.reportFiles) {
      payload.reportFiles.forEach((file) => {
        formData.append('reportFiles', file);
      });
    }

    if (payload.mainFileType) {
      formData.append('mainFileType', payload.mainFileType);
    }

    if (payload.selectedDCMId) {
      formData.append('selectedDCMId', payload.selectedDCMId);
    }

    return this.sendFormData('PUT', `/cases/${payload.caseId}/clinic-upload`, formData, progress$);
  }

  submitOnlinePortalAccess(payload) {
    return this.put(`/cases/${payload.caseId}/share-online-portal-access`, payload);
  }

  addAdditionalFile(payload, progress$ = new BehaviorSubject(0)) {
    const formData = this.payloadToFormData(payload, ['files']);

    if (payload.files) {
      payload.files.forEach((file) => {
        formData.append('files', file);
      });
    }

    return this.sendFormData('POST', `/cases/${payload.caseId}/additionalFiles`, formData, progress$);
  }
}
